import NStatusChip from "@/components/common/NStatusChip";
import RecommendationChip from "@/components/common/RecommendationChip";
import { isFieldTrial } from "@/lib/utils/access";
import { FieldInsights } from "@/types/fieldInsights";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { MoistureStatus } from "@/types/water";
import { Card, CardFooter, CardHeader, Chip } from "@heroui/react";
import { ProcessingStatus } from "@/types/fieldNitrogenPlan";
import {
    getSimulationTypeColor,
    getSimulationTypeLabel,
} from "@/lib/utils/nitrogenPlan";
import { getRateValue, FormatUnit } from "@/components/home/InsightsCells";
import { EnrollmentTier } from "@/lib/utils/enrollment";

type InsightsMobileFieldCardProps = {
    field: FieldInsights;
    growersById: Record<number, Organization>;
    isConsultantOrSentinel: boolean;
    user: User | null | undefined;
    formatUnit: FormatUnit;
    viewAllRates: boolean;
    onPress?: () => void;
};

function getWaterChipColor(
    moistureStatus?: MoistureStatus,
): "success" | "primary" | "warning" | "danger" | "default" {
    if (moistureStatus === MoistureStatus.OPTIMAL) return "success";
    if (moistureStatus === MoistureStatus.APPROACHING_SATURATION) return "primary";
    if (moistureStatus === MoistureStatus.APPROCHING_DEFICIENCY) return "danger";

    return "default";
}

export default function InsightsMobileFieldCard({
    field,
    growersById,
    isConsultantOrSentinel,
    user,
    formatUnit,
    viewAllRates,
    onPress,
}: InsightsMobileFieldCardProps) {
    const growerName = growersById[field.grower]?.name;

    const trialField = !!user && isFieldTrial(field, user);

    const recommendation = field.recommendation;

    const cropName =
        field.active_planting_operation?.crop_configuration?.crop_name;

    const growthStage = field.crop_stage?.growth_stage;

    const waterEstimate = field.water?.water_estimate;
    const waterCapacity = waterEstimate?.capacity_percentage;
    const waterStatus = waterEstimate?.moisture_status as MoistureStatus | undefined;

    const fieldHasWaterInsights =
        field.active_enrollment?.enrollment_tier ===
        EnrollmentTier.Irrigation ||
        field.active_enrollment?.enrollment_tier === EnrollmentTier.Complete;

    const plan = field.default_nitrogen_plan_simulation;

    const showRate =
        !trialField &&
        !!recommendation &&
        (recommendation.recommend_fertigation || viewAllRates) &&
        plan?.processing_status === ProcessingStatus.COMPLETED;

    return (
        <Card
            isPressable={!!onPress}
            onPress={onPress}
            className="w-full hover:bg-gray-200"
            shadow="sm"
        >
            <CardHeader className="pb-1 px-3">
                <div className="flex w-full items-start justify-between gap-2 text-left">
                    <div className="flex flex-1 flex-col items-start min-w-0 text-left">
                        <p className="w-full text-left font-semibold text-lg truncate">
                            {field.name}
                            {field.crop_zone_name ? (
                                <span
                                    className="text-xs font-medium ml-1.5"
                                    style={
                                        field.crop_zone_color
                                            ? { color: field.crop_zone_color }
                                            : undefined
                                    }
                                >
                                    {field.crop_zone_name}
                                </span>
                            ) : null}
                        </p>

                        {isConsultantOrSentinel && growerName && (
                            <p className="w-full text-left text-sm truncate">
                                {growerName}
                            </p>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-row items-center justify-end gap-1">
                        {cropName && (
                            <Chip
                                classNames={{
                                    base: "bg-gray-100",
                                    content: "text-gray-900 font-bold",
                                }}
                                size="sm"
                                radius="sm"
                            >
                                {cropName}
                            </Chip>
                        )}

                        {growthStage && (
                            <Chip
                                classNames={{
                                    base: "bg-gray-100",
                                    content: "text-gray-900 font-bold",
                                }}
                                size="sm"
                                radius="sm"
                            >
                                {growthStage}
                            </Chip>
                        )}
                    </div>
                </div>
            </CardHeader>

            <CardFooter className="text-xs pt-0 px-3">
                <div className="flex flex-col w-full gap-1">
                    {showRate && plan && (
                        <div className="flex w-full flex-row items-center gap-1 overflow-hidden">
                            <Chip
                                size="sm"
                                radius="sm"
                                color="primary"
                                variant="flat"
                                className="shrink-0"
                            >
                                {getRateValue({
                                    trialField,
                                    recommendation,
                                    viewAllRates,
                                    plan,
                                    formatUnit,
                                })}
                            </Chip>

                            <div
                                className="shrink-0 max-w-[110px] truncate rounded-md flex items-center justify-center px-1.5 py-0.5 text-[0.65rem] font-bold"
                                style={{
                                    backgroundColor: getSimulationTypeColor(
                                        plan.simulation_type,
                                    ),
                                }}
                            >
                                {getSimulationTypeLabel(plan.simulation_type)}
                            </div>
                        </div>
                    )}

                    <div className="flex w-full flex-row items-center gap-1 overflow-hidden">
                        {trialField ? (
                            <div className="shrink-0">
                                <Chip
                                    color="warning"
                                    variant="flat"
                                    radius="sm"
                                    size="sm"
                                >
                                    Trial Field
                                </Chip>
                            </div>
                        ) : recommendation?.n_status ? (
                            <div className="shrink-0">
                                <NStatusChip
                                    nStatus={recommendation.n_status}
                                />
                            </div>
                        ) : null}

                        {!trialField && recommendation && (
                            <div className="shrink-0 max-w-[92px] overflow-hidden">
                                <RecommendationChip
                                    recommendation={recommendation}
                                    notes={
                                        recommendation.user_recommendation
                                            ?.notes ?? undefined
                                    }
                                    user={
                                        recommendation.user_recommendation
                                            ?.user ?? undefined
                                    }
                                    deferDoNotApply={true}
                                />
                            </div>
                        )}

                        {!trialField && fieldHasWaterInsights && (
                            <Chip
                                color={
                                    waterEstimate
                                        ? getWaterChipColor(waterStatus)
                                        : "default"
                                }
                                variant="flat"
                                size="sm"
                                radius="sm"
                                classNames={{
                                    base: "shrink-0 px-2",
                                    content: "font-bold truncate",
                                }}
                            >
                                H2O{" "}
                                {waterCapacity !== undefined
                                    ? `${waterCapacity.toFixed(1)}%`
                                    : "-"}
                            </Chip>
                        )}
                    </div>
                </div>
            </CardFooter>
        </Card>
    );
}
